import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { Copy, Check, Clock, Zap, ArrowLeft, ShieldCheck, ExternalLink, AlertTriangle, Loader2 } from "lucide-react";
import { getOrderPayment, getOrder } from "../lib/api";
import { toast } from "sonner";

const POLL_INTERVAL_MS = 8000;
const TERMINAL = new Set(["paid", "failed", "expired", "refunded"]);
const PAID = new Set(["paid"]);

const STATUS_COPY = {
  waiting: { label: "Waiting for payment", emoji: "⏳", tone: "neutral" },
  confirming: { label: "Payment detected · waiting for confirmation", emoji: "🔎", tone: "info" },
  paid: { label: "Payment confirmed · funds in escrow", emoji: "✅", tone: "success" },
  partially_paid: { label: "Partial payment received", emoji: "⚠️", tone: "warn" },
  failed: { label: "Payment failed", emoji: "❌", tone: "error" },
  expired: { label: "Payment window expired", emoji: "⌛", tone: "error" },
  refunded: { label: "Refunded", emoji: "↩️", tone: "neutral" },
};

const TONE = {
  neutral: "border-[#15152a] text-[#a0a0b0]",
  info: "border-[#00bbff]/30 text-[#7fdfff]",
  success: "border-[#00ff88]/40 text-[#00ff88]",
  warn: "border-[#ffaa00]/40 text-[#ffd566]",
  error: "border-[#ff4444]/40 text-[#ff8888]",
};

function qrUrl(text, size = 220) {
  // Free public QR generator — no extra dep needed.
  return `https://api.qrserver.com/v1/create-qr-code/?data=${encodeURIComponent(text)}&size=${size}x${size}&bgcolor=06060c&color=00ff88&qzone=1`;
}

function useCountdown(target) {
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, []);
  if (!target) return null;
  const ms = Math.max(0, new Date(target).getTime() - now);
  const m = Math.floor(ms / 60000);
  const s = Math.floor((ms % 60000) / 1000);
  return { ms, label: `${m}:${String(s).padStart(2, "0")}`, expired: ms <= 0 };
}

export default function PayLtc() {
  const { orderId } = useParams();
  const nav = useNavigate();
  const [order, setOrder] = useState(null);
  const [payment, setPayment] = useState(null);
  const [loading, setLoading] = useState(true);
  const [copiedAddr, setCopiedAddr] = useState(false);
  const [copiedAmount, setCopiedAmount] = useState(false);
  const pollRef = useRef(null);

  // Initial load
  useEffect(() => {
    (async () => {
      try {
        const [o, p] = await Promise.all([getOrder(orderId), getOrderPayment(orderId)]);
        setOrder(o);
        setPayment(p);
      } catch (_) {
        toast.error("Could not load order");
      } finally {
        setLoading(false);
      }
    })();
  }, [orderId]);

  // Poll status until terminal
  useEffect(() => {
    if (!payment) return;
    const status = payment?.ltc?.payment_status;
    if (TERMINAL.has(status)) return;
    pollRef.current = setInterval(async () => {
      try {
        const p = await getOrderPayment(orderId);
        setPayment(p);
        if (TERMINAL.has(p?.ltc?.payment_status)) clearInterval(pollRef.current);
        if (PAID.has(p?.ltc?.payment_status) && p?.order_status === "paid") clearInterval(pollRef.current);
      } catch (_) { /* keep polling */ }
    }, POLL_INTERVAL_MS);
    return () => clearInterval(pollRef.current);
  }, [orderId, payment?.ltc?.payment_status]);

  const ltc = payment?.ltc;
  const cd = useCountdown(ltc?.expiration_estimate_date);
  const status = ltc?.payment_status || "waiting";
  const copy = STATUS_COPY[status] || STATUS_COPY.waiting;
  const tone = TONE[copy.tone] || TONE.neutral;
  const isPaidUnlocked = useMemo(() => PAID.has(status) || payment?.order_status === "paid", [status, payment?.order_status]);

  const copyText = async (txt, setter) => {
    try {
      await navigator.clipboard.writeText(txt);
      setter(true);
      setTimeout(() => setter(false), 1500);
    } catch (_) {
      toast.error("Copy failed");
    }
  };

  if (loading) return <div data-testid="pay-loading" className="max-w-3xl mx-auto px-6 py-20 text-[#606070]">Loading payment…</div>;
  if (!order) return <div className="max-w-3xl mx-auto px-6 py-20 text-[#606070]">Order not found.</div>;

  if (order.payment_method !== "ltc") {
    return (
      <div className="max-w-2xl mx-auto px-6 py-20 text-center">
        <div className="text-sm text-[#a0a0b0] mb-4">This order was paid with card.</div>
        <Link to={`/orders/${order.id}`} className="cc-btn-primary inline-flex px-5 py-2 rounded-lg">Go to order</Link>
      </div>
    );
  }

  return (
    <div data-testid="pay-ltc-page" className="max-w-5xl mx-auto px-6 py-12">
      <Link to={`/orders/${order.id}`} className="text-xs text-[#a0a0b0] hover:text-white inline-flex items-center gap-1 mb-4">
        <ArrowLeft size={12} /> Back to order {order.id}
      </Link>

      <div className="grid lg:grid-cols-[1fr_320px] gap-6">
        {/* Payment card */}
        <div className="cc-elevated rounded-2xl p-7 border border-[#15152a]">
          <div className="flex items-center gap-2 mb-1">
            <Zap size={14} className="text-[#00ff88]" />
            <span className="cc-overline">Pay with Litecoin</span>
          </div>
          <h1 className="text-2xl md:text-3xl font-semibold tracking-tight text-white mt-1">
            Send <span className="font-mono text-[#00ff88]">{ltc?.pay_amount} LTC</span>
          </h1>
          <div className="text-sm text-[#a0a0b0] mt-1">≈ ${ltc?.price_amount} · expires in {cd?.expired ? "—" : cd?.label || "…"}</div>

          {/* Status pill */}
          <div data-testid="pay-status" className={`mt-5 inline-flex items-center gap-2 px-3 py-1.5 rounded-md border bg-[#06060c] ${tone}`}>
            {(status === "waiting" || status === "confirming") && <Loader2 size={12} className="animate-spin" />}
            <span className="text-xs">{copy.emoji} {copy.label}</span>
          </div>

          {/* QR + Address */}
          {ltc?.pay_address && (
            <div className="grid sm:grid-cols-[220px_1fr] gap-6 mt-7 items-start">
              <div className="rounded-xl p-3 border border-[#15152a] bg-[#06060c]">
                <img
                  data-testid="pay-qr"
                  src={qrUrl(`litecoin:${ltc.pay_address}?amount=${ltc.pay_amount}`)}
                  alt="LTC payment QR"
                  className="w-full h-auto rounded-md"
                />
                <div className="text-[10px] text-[#606070] uppercase tracking-widest text-center mt-2">Scan to pay</div>
              </div>

              <div className="space-y-3">
                <Field label="Litecoin address">
                  <div className="flex items-center gap-2">
                    <span data-testid="pay-address" className="font-mono text-xs text-white break-all">{ltc.pay_address}</span>
                    <button
                      data-testid="pay-copy-address"
                      onClick={() => copyText(ltc.pay_address, setCopiedAddr)}
                      className="shrink-0 p-1.5 border border-[#15152a] rounded-md text-[#a0a0b0] hover:text-[#00ff88]"
                      title="Copy address"
                    >
                      {copiedAddr ? <Check size={12} /> : <Copy size={12} />}
                    </button>
                  </div>
                </Field>

                <Field label="Exact amount in LTC">
                  <div className="flex items-center gap-2">
                    <span data-testid="pay-amount" className="font-mono text-2xl text-[#00ff88]">{ltc.pay_amount}</span>
                    <span className="text-xs text-[#606070]">LTC</span>
                    <button
                      data-testid="pay-copy-amount"
                      onClick={() => copyText(String(ltc.pay_amount), setCopiedAmount)}
                      className="ml-2 p-1.5 border border-[#15152a] rounded-md text-[#a0a0b0] hover:text-[#00ff88]"
                      title="Copy amount"
                    >
                      {copiedAmount ? <Check size={12} /> : <Copy size={12} />}
                    </button>
                  </div>
                  <div className="text-[11px] text-[#606070] mt-0.5">≈ ${ltc.price_amount}</div>
                </Field>

                {(ltc?.expires_at || ltc?.expiration_estimate_date) && !cd?.expired && (
                  <div data-testid="pay-countdown" className="inline-flex items-center gap-1.5 text-[11px] text-[#a0a0b0] border border-[#ffaa00]/25 bg-[#ffaa00]/5 px-2 py-1 rounded-md">
                    <Clock size={11} /> Pay within <span className="font-mono">{cd?.label}</span>
                  </div>
                )}
                {cd?.expired && status === "waiting" && (
                  <div className="inline-flex items-center gap-1.5 text-[11px] text-[#ff8888] border border-[#ff4444]/25 bg-[#ff4444]/5 px-2 py-1 rounded-md">
                    <AlertTriangle size={11} /> Window expired — generate a new order.
                  </div>
                )}
              </div>
            </div>
          )}

          {ltc?.tx_hash && (
            <a
              data-testid="pay-tx-hash"
              href={`https://blockchair.com/litecoin/transaction/${ltc.tx_hash}`}
              target="_blank" rel="noreferrer"
              className="mt-5 inline-flex items-center gap-1.5 text-xs text-[#00ff88] hover:underline font-mono break-all"
            >
              {ltc.tx_hash} <ExternalLink size={11} />
            </a>
          )}

          {isPaidUnlocked && (
            <div data-testid="pay-success-cta" className="mt-7 p-4 rounded-lg border border-[#00ff88]/30 bg-[#00ff88]/5 flex items-start gap-3">
              <ShieldCheck size={18} className="text-[#00ff88] mt-0.5" />
              <div className="flex-1">
                <div className="text-sm text-white font-medium">Your order is now active</div>
                <div className="text-xs text-[#a0a0b0] mt-0.5">Funds are in escrow with CHEATCRAFT until you approve the delivery.</div>
              </div>
              <button onClick={() => nav(`/orders/${order.id}`)} className="cc-btn-primary rounded-lg px-4 py-2 text-xs">
                Open order
              </button>
            </div>
          )}
        </div>

        {/* Sidebar — order summary */}
        <aside className="cc-surface rounded-2xl p-5 h-fit border border-[#15152a]">
          <div className="cc-overline mb-3">Order</div>
          <div className="text-xs text-[#606070] font-mono mb-1">{order.id}</div>
          <div className="text-sm text-white font-medium">{order.creator_name}</div>
          <div className="text-xs text-[#a0a0b0] mb-4">{order.content_type} · {order.game}</div>

          <Row label="Base" value={`$${order.base_price ?? order.price}`} />
          {(order.upsells || []).map((u) => <Row key={u.id} label={`+ ${u.name}`} value={`$${u.price}`} accent />)}
          <div className="border-t border-[#15152a] my-2" />
          <Row label="Total" value={`$${order.price}`} bold />
          <Row label="In LTC" value={<span className="font-mono text-[#00ff88]">{ltc?.pay_amount} LTC</span>} bold />

          <div className="mt-4 text-[10px] text-[#606070] leading-relaxed inline-flex items-start gap-1.5">
            <span className="w-1.5 h-1.5 rounded-full bg-[#ffaa00] mt-1.5 shrink-0" />
            <span>Funds remain in escrow until you approve the delivery — full refund if creator misses their max delivery time.</span>
          </div>
        </aside>
      </div>
    </div>
  );
}

const Field = ({ label, children }) => (
  <div>
    <div className="cc-overline mb-1">{label}</div>
    {children}
  </div>
);

const Row = ({ label, value, bold, accent }) => (
  <div className="flex items-center justify-between py-1">
    <span className={`text-xs ${bold ? "text-white font-semibold" : "text-[#a0a0b0]"}`}>{label}</span>
    <span className={`text-xs font-mono ${accent ? "text-[#00ff88]" : bold ? "text-white" : "text-white"}`}>{value}</span>
  </div>
);
